import { PayloadAction } from '@reduxjs/toolkit';
import { ApiResponse } from 'apisauce';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import {
  Observable,
  Observer,
  of,
  Subscription,
  timer,
} from 'rxjs';
import {
  concatMap,
  debounceTime,
  delay,
  filter,
  map,
  mergeMap,
} from 'rxjs/operators';

import config from '../../../config';
import { MOCK } from '../../../configDebug';
import LibraryConfig from '../../../configLib';
import {
  EnrollChanelEnum,
  HttpStatusCodeEnum,
  PredefinedMilisecondsTypeEnum,
} from '../../enums';
import { CALLS_TO_MEMOIZE, UPDATE_BALANCES_ORDER_DELAY } from '../../libSettings';
import {
  AccountSummary,
  AutoPassRequest,
  AutoPassResponse,
  ModifyOwnBirthdateRequest,
  SearchTradingPlansRequest,
  TradingPlanStatus,
  UserTypeRelation,
  CreateReferralPayoutRequest,
  ReferralPagedRequest,
} from '../../models/crm/types';
import {
  ENROLL_METHOD,
  IndividualStatusEnum,
} from '../../models/enroll';
import { GenderTypeEnum } from '../../models/enroll-requests';
import { MarketCountryCode } from '../../models/fundamental/types';
import { CRMService } from '../../services/CRM.service';
import GlobalService from '../../services/Global.service';
import { LoggerService } from '../../services/logger/Logger.service';
import CountriesCache, { updateCountriesCache } from '../../store-util/CountriesCache';
import CustomerState from '../../store-util/CustomerState';
import {
  clearDemoAccountActivating,
  isClosedAccountStatus,
  isDemoAccountReference,
  isPendingAccountStatus,
  isUsableAccountStatus,
  markDemoAccountActivating,
} from '../../util/account-helpers';
import { isCallStatusReady } from '../../util/error-handling/StatusByCallHelpers';
import { isEmpty } from '../../util/typeGuards';
import {
  clearPlanPurchasePaymentCallback,
  getPaymentReferenceId,
  isPlanPurchasePaymentCallback,
} from '../../util/url-helpers';
import {
  accountsFailed,
  applicationStartup,
  enrollAuthCompleted,
  getAuthUserTradingAcount,
  getBalances,
  getCountryListSuccess,
  getIndividualExtendedInfo,
  individualExtendedInfoFailed,
  individualExtendedInfoSuccess,
  logout,
  servicesReady,
  sorOrderEvent,
} from '../common-actions';
import { processErrorInResponse, processResponse } from '../helpers';
import { getCardTransferStatusSuccess } from '../payment';
import { ALL_TRANSACTIONS_PAGE_SIZE, FIRST_PAGE_NUMBER } from '../payment/constants';
import { isTradingPlanPaymentProcessed } from '../payment/helpers';
import {
  hasValidCookie,
  isUserVerifiedStatus,
} from '../selectors';
import { getBuyingPower } from '../trading';
import { hasBPUpdated } from '../trading/helpers';
import { RootState } from '..';

import {
  INDIVIDUAL_EXTENDED_INFO_CALL_LIMIT,
  INDIVIDUAL_EXTENDED_INFO_CALL_NAME,
} from './constants';
import {
  getCurrentAccount,
  hasAnyAccountPaidPlan,
} from './helpers';
import {
  accountsCompleted,
  activateDemoAccount,
  activateDemoAccountFail,
  activateDemoAccountSuccess,
  couponPreview,
  couponPreviewFail,
  couponPreviewSuccess,
  createPlanPayment,
  createPlanPaymentFail,
  createPlanPaymentSuccess,
  createPurchasePayment,
  createPurchasePaymentFail,
  createPurchasePaymentSuccess,
  getAutoPass,
  getAutoPassFail,
  getAutoPassSuccess,
  getReferralOwnProfile,
  getReferralOwnProfileFailed,
  getReferralOwnProfileSuccess,
  getReferralOwnQr,
  getReferralOwnQrFailed,
  getReferralOwnQrSuccess,
  getReferralOwnReferredUsers,
  getReferralOwnReferredUsersFailed,
  getReferralOwnReferredUsersSuccess,
  getReferralOwnCommissions,
  getReferralOwnCommissionsFailed,
  getReferralOwnCommissionsSuccess,
  createReferralOwnPayoutRequest,
  createReferralOwnPayoutRequestFailed,
  createReferralOwnPayoutRequestSuccess,
  getCountryList,
  getCountryListFailed,
  getTradingPlatforms,
  getTradingPlatformsFailed,
  getTradingPlatformsSuccess,
  modifyOwnBirthdate,
  modifyOwnBirthdateFail,
  modifyOwnBirthdateSuccess,
  searchPlanDetails,
  searchPlanDetailsFail,
  searchPlanDetailsSuccess,
  searchTradingPlans,
  searchTradingPlansFail,
  searchTradingPlansSuccess,
  setPendingTradingAccount,
  setUseDemoAccount,
  updateAutoPass,
  updateAutoPassFail,
  updateAutoPassSuccess,
  updateIndividualTransitionFromNewToForVerification,
  updateIndividualTransitionFromNewToForVerificationFail,
  updateIndividualTransitionFromNewToForVerificationSuccess,
} from './index';
import { AccountsCompletedPayload, CRMCall } from './types';

const logger = LoggerService.getLogger();

const extendedInfoEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => {
  let subscription: Subscription;
  let callCount = 0;

  return action$.pipe(
    ofType(
      getIndividualExtendedInfo.type,
      activateDemoAccountSuccess.type,
      getCardTransferStatusSuccess.type,
      servicesReady.type,
    ),
    filter(action => !(!action.payload?.skipCookieCheck && !hasValidCookie(state$.value))),
    mergeMap(action => new Observable((observer: Observer<any>) => {
      let callName = INDIVIDUAL_EXTENDED_INFO_CALL_NAME;
      const isPlanPaymentProcessed = isTradingPlanPaymentProcessed(state$.value.payment);

      if (callCount >= INDIVIDUAL_EXTENDED_INFO_CALL_LIMIT) {
        logger.info('The call count exceeds the limit! Redirecting user to the login');
        subscription?.unsubscribe();
        callCount = 0;
        observer.next(logout());
        return;
      }

      GlobalService
        .crmService
        .enrollGet(ENROLL_METHOD[ENROLL_METHOD.getIndividualExtendedInfo])
        .then((response: ApiResponse<any>) => {
          if (isPlanPaymentProcessed && response.data?.individual_status !== IndividualStatusEnum.Active) {
            logger.info('There user is not verified! The same endpoint is called repeatedly by waiting for 3 seconds');
            subscription?.unsubscribe();
            subscription = timer(PredefinedMilisecondsTypeEnum.threeSeconds)
              .subscribe(() => {
                callCount++;
                observer.next(getIndividualExtendedInfo(action.payload)); // Re-dispatch the action
              });
            return;
          }

          subscription?.unsubscribe();
          callCount = 0;
          processResponse(
            callName,
            {},
            response,
            observer,
            individualExtendedInfoSuccess,
            individualExtendedInfoFailed,
          );
        })
        .catch(error => processErrorInResponse(callName, error, action, observer, individualExtendedInfoFailed));
    })),
  );
};

const getAccountReferenceIdListEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => (
  action$.pipe(
    ofType(individualExtendedInfoSuccess.type, getAuthUserTradingAcount.type),
    filter(action => !state$.value.auth.enrollPending),
    filter(action => isUserVerifiedStatus(action.payload) || MOCK.ENABLED),
    filter(action => hasValidCookie(state$.value)),
    concatMap(action => new Observable((observer: Observer<any>) => {
      const callName = 'crm/accounts';

      let responseAccounts: AccountSummary[]|undefined = [];
      const userRelationType = CustomerState.get().userTypeRelation ?? UserTypeRelation.Individual;
      const payload = {
        customerId: state$.value.crm.individualOrCompanyEmployeeId as number,
        userTypeRelation: userRelationType as number,
      };

      GlobalService
        .crmService
        .getAccountsSummary(payload)
        .then((response: ApiResponse<AccountSummary[]>) => {
          if (response.status === HttpStatusCodeEnum.NotFound) {
            return { ...response, data: [] };
          }

          const hasAccountPaidPLan = hasAnyAccountPaidPlan(
            state$.value.crm,
            (response.data ?? []).filter(account => !account.is_cash_account),
          );
          const paymentReferenceId = getPaymentReferenceId();
          const isPlanCallback = isPlanPurchasePaymentCallback();
          const isAccountNonClosed = (item: AccountSummary) => !item.is_cash_account
            && !isClosedAccountStatus(item.account_status);
          const hasNonClosedAccount = (response.data ?? []).some(isAccountNonClosed);

          if (!isEmpty(response.data) && paymentReferenceId
            && isPlanCallback && (!hasAccountPaidPLan || !hasNonClosedAccount)) {
            return { ...response, data: [] };
          }

          responseAccounts = response.data ?? [];

          // Set isUseDemoAccount to false after successful plan purchase
          if (isPlanCallback && hasAccountPaidPLan) {
            observer.next(setUseDemoAccount(false));
          }

          const data = isPlanCallback
            ? responseAccounts.filter(isAccountNonClosed).map(item => item.reference_id)
            : responseAccounts.filter(item => !item.is_cash_account).map(item => item.reference_id);

          const tradingAccounts = responseAccounts.filter(item => !item.is_cash_account);
          const hasUsableTradingAccount = tradingAccounts.some(item => !isPendingAccountStatus(item.account_status));

          // The accounts are not stored while they are still being created, so the one being created
          // is recorded on its own - the setup notification has nothing else to go by when the
          // session markers are missing, and its reference id says whether it is the demo account.
          const pendingTradingAccount = hasUsableTradingAccount ? undefined : tradingAccounts[0];
          observer.next(setPendingTradingAccount(pendingTradingAccount?.reference_id ?? null));

          if (hasUsableTradingAccount) {
            clearDemoAccountActivating();
          }

          // The purchased account is there, so the plan callback is over - it is the same marker the
          // poll used to clear when it stopped.
          if (responseAccounts.some(item => !item.is_cash_account
            && !isDemoAccountReference(item.reference_id)
            && isUsableAccountStatus(item.account_status))) {
            clearPlanPurchasePaymentCallback();
          }

          return { ...response, data: hasUsableTradingAccount ? data : [] };
        })
        .then((response: ApiResponse<any>) => {
          if (isEmpty(response.data)) {
            logger.info('There is no usable trading account in the response');
            observer.complete();
            return;
          }

          const accountsPayload: AccountsCompletedPayload = {
            referenceIds: response.data ?? [],
            accounts: responseAccounts ?? [],
          };
          processResponse(
            callName,
            {},
            response,
            observer,
            accountsCompleted,
            accountsFailed,
            false,
            responseAccounts,
            accountsPayload,
          );
          observer.complete();
        })
        .catch(error => {
          processErrorInResponse(callName, error, action, observer, accountsFailed);
          observer.complete();
        });
    })),
  )
);

const getBalancesEpic = (action$: Observable<any>) => action$.pipe(
  ofType(sorOrderEvent.type),
  debounceTime(UPDATE_BALANCES_ORDER_DELAY),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const { status } = action.payload;

    if (hasBPUpdated(status, MarketCountryCode.UnitedStates)) {
      observer.next(getBuyingPower());
    } else {
      observer.next(getBalances());
    }
  })),
);

const getCountryListEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(applicationStartup.type, getCountryList.type),
  filter(() => !CountriesCache.get()?.length || !CALLS_TO_MEMOIZE.getCountryList),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/getCountryList';

    GlobalService
      .crmService
      .getCountryList()
      .then(response => {
        if (processResponse(callName, null, response, observer, getCountryListSuccess, getCountryListFailed)) {
          updateCountriesCache(response.data);
        } else {
          updateCountriesCache([]);
        }
      })
      .catch(error => {
        updateCountriesCache([]);
        processErrorInResponse(callName, error, action, observer, getCountryListFailed);
      });
  })),
);

const getTradingPlatformsEpic = (action$: Observable<any>) => action$.pipe(
  ofType(getTradingPlatforms.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'getTradingPlatforms';
    GlobalService
      .crmService
      .getTradingPlatforms()
      .then((response: any) => {
        processResponse(
          callName,
          null,
          response,
          observer,
          getTradingPlatformsSuccess,
          getTradingPlatformsFailed,
          false,
          response,
        );
      })
      .catch((error: any) => processErrorInResponse(callName, error, action, observer));
  })),
);

const createIndividualEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getTradingPlatformsSuccess.type),
  filter(() => !state$.value.crm.individualExtendedInfo),
  filter(() => state$.value.crm.tradingPlatformId !== undefined),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    const {
      enroll: {
        email,
        firstName,
        lastName,
        country,
      },
    } = state$.value.auth;
    const { tradingPlatformId } = state$.value.crm;

    const temporaryCrmService = new CRMService();

    // create individual
    const callMethodIndividual = ENROLL_METHOD[ENROLL_METHOD.postEnrollmentOwnPersonal];
    const requestBodyIndividual = {
      first_name: firstName,
      last_name: lastName,
      birth_place_id: country,
      nationalities: [ country ],
      gender: GenderTypeEnum.unspecified,
      enroll_channel: LibraryConfig.isWeb ? EnrollChanelEnum.Web : EnrollChanelEnum.Mobile,
      trading_platform_id: tradingPlatformId,
    } as any;

    requestBodyIndividual.initially_selected_plan_id = state$.value.crm.enroll.initiallySelectedPlanId;
    requestBodyIndividual.initially_selected_auto_pass = state$.value.crm.enroll.initiallySelectedAutoPass;
    // The ACE referral program code from the invitation link (?ref=CODE). Distinct from the
    // marketing referral_code field - this is what binds the new client to their referrer, and
    // attribution happens only here, when the individual is first created.
    requestBodyIndividual.referral_program_code = state$.value.crm.enroll.initiallySelectedReferralCode;

    // create residential address
    const callMethodAddress = ENROLL_METHOD[ENROLL_METHOD.postIndividualAddressOwn];
    const requestBodyAddress = {
      address_type: 'R',
      country_id: country,
    };

    temporaryCrmService
      .enrollPost(callMethodIndividual, requestBodyIndividual)
      .then(response => {
        // TODO: error handling if respone body has error
        temporaryCrmService.enrollPost(callMethodAddress, requestBodyAddress);
      })
      .catch(error => {
        // TODO: error handling
      });
  })),
);

const activateDemoAccountEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(activateDemoAccount.type),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/activateDemoAccount';
    markDemoAccountActivating();

    GlobalService
      .crmService
      .activateDemoAccount(state$.value.crm.individualOrCompanyEmployeeId as number)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          activateDemoAccountSuccess,
          activateDemoAccountFail,
          false,
          {},
        );
      }))
      .catch(error => {
        clearDemoAccountActivating();
        processErrorInResponse(callName, error, action, observer, activateDemoAccountFail);
      });
  })),
);

const updateIndividualTransitionFromNewToForVerificationEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(updateIndividualTransitionFromNewToForVerification.type),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/updateIndividualTransitionFromNewToForVerification';

    GlobalService
      .crmService
      .updateIndividualTransitionFromNewToForVerification(state$.value.crm.individualOrCompanyEmployeeId as number)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          updateIndividualTransitionFromNewToForVerificationSuccess,
          updateIndividualTransitionFromNewToForVerificationFail,
          false,
          {},
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        updateIndividualTransitionFromNewToForVerificationFail,
      ));
  })),
);

const modifyOwnBirthdateEpic = (
  action$: Observable<any>,
) => action$.pipe(
  ofType(modifyOwnBirthdate.type),
  mergeMap((action: PayloadAction<ModifyOwnBirthdateRequest>) => new Observable((observer: Observer<any>) => {
    const callName = 'crm/modifyOwnBirthdate';
    const payload = action.payload;

    GlobalService
      .crmService
      .modifyOwnBirthdate(payload)
      .then(response => {
        processResponse(
          callName,
          payload,
          response,
          observer,
          modifyOwnBirthdateSuccess,
          modifyOwnBirthdateFail,
          false,
          undefined,
          payload.birthdate,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        modifyOwnBirthdateFail,
      ));
  })),
);

const createPlanPaymentEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(createPlanPayment.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/createPlanPayment';

    GlobalService
      .crmService
      .createPlanPayment(action.payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          createPlanPaymentSuccess,
          createPlanPaymentFail,
        );
      }));
  })),
);

const couponPreviewEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(couponPreview.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/couponPreview';

    GlobalService
      .crmService
      .couponPreview(action.payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          couponPreviewSuccess,
          couponPreviewFail,
        );
      }));
  })),
);

const createPurchasePaymentEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(createPurchasePayment.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/createPurchasePayment';

    GlobalService
      .crmService
      .createPurchasePayment(action.payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          createPurchasePaymentSuccess,
          createPurchasePaymentFail,
        );
      }));
  })),
);

const searchTradingPlansEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(searchTradingPlans.type, getCountryListSuccess.type, individualExtendedInfoSuccess.type),
  filter(action => action.type === searchTradingPlans.type
    || !isCallStatusReady(CRMCall.searchTradingPlans, state$.value.crm.statusByCall)),
  mergeMap(action => new Observable((observer: Observer<SearchTradingPlansRequest>) => {
    const callName = 'crm/searchTradingPlans';

    const defaultRequest: SearchTradingPlansRequest = {
      page_number: FIRST_PAGE_NUMBER,
      page_size: ALL_TRANSACTIONS_PAGE_SIZE,
      order_column: 'ReferenceId',
      order_ascending: true,
      tenant_id: config.auth.tenantId,
      is_demo: true,
      trading_plan_status_enum: TradingPlanStatus.ActiveAndPurchasable,
    };

    // Use request parameters from action payload if provided and valid, otherwise use defaults
    let request: SearchTradingPlansRequest = defaultRequest;

    if (action.type === searchTradingPlans.type && action.payload && typeof action.payload === 'object' && !Array.isArray(action.payload)) {
      request = action.payload;
    }

    GlobalService
      .crmService
      .searchTradingPlans(request)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          searchTradingPlansSuccess,
          searchTradingPlansFail,
        );
      }))
      .catch(error => processErrorInResponse(callName, error, action, observer, searchTradingPlansFail));
  })),
);

const searchPlanDetailsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(searchPlanDetails.type),
  mergeMap(action => new Observable((observer: Observer<SearchTradingPlansRequest>) => {
    const callName = 'crm/searchPlanDetails';
    GlobalService
      .crmService
      .searchTradingPlans(action.payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          searchPlanDetailsSuccess,
          searchPlanDetailsFail,
        );
      }));
  })),
);

const getAutoPassEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getAutoPass.type),
  mergeMap(action => new Observable((observer: Observer<AutoPassResponse>) => {
    const callName = 'crm/getAutoPass';

    GlobalService
      .crmService
      .getAutoPass()
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getAutoPassSuccess,
          getAutoPassFail,
        );
      }));
  })),
);

const updateAutoPassEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(updateAutoPass.type),
  mergeMap((action: PayloadAction<AutoPassRequest>) => new Observable((observer: Observer<AutoPassResponse>) => {
    const callName = 'crm/updateAutoPass';

    GlobalService
      .crmService
      .updateAutoPass(action.payload)
      .then((response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          updateAutoPassSuccess,
          updateAutoPassFail,
        );
      }))
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        updateAutoPassFail,
      ));
  })),
);

const getReferralOwnProfileEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getReferralOwnProfile.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/getReferralOwnProfile';

    GlobalService
      .crmService
      .getReferralOwnProfile()
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getReferralOwnProfileSuccess,
          getReferralOwnProfileFailed,
        );
      })
      .catch(error => {
        processErrorInResponse(callName, error, action, observer, getReferralOwnProfileFailed);
      });
  })),
);

const getReferralOwnQrEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getReferralOwnQr.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'crm/getReferralOwnQr';

    GlobalService
      .crmService
      .getReferralOwnQr()
      .then(response => {
        processResponse(callName, {}, response, observer, getReferralOwnQrSuccess, getReferralOwnQrFailed);
      })
      .catch(error => {
        processErrorInResponse(callName, error, action, observer, getReferralOwnQrFailed);
      });
  })),
);

const getReferralOwnReferredUsersEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getReferralOwnReferredUsers.type),
  mergeMap((action: PayloadAction<ReferralPagedRequest | undefined>) => new Observable(
    (observer: Observer<any>) => {
      const callName = 'crm/getReferralOwnReferredUsers';

      GlobalService
        .crmService
        .getReferralOwnReferredUsers(action.payload)
        .then(response => {
          processResponse(
            callName,
            action.payload,
            response,
            observer,
            getReferralOwnReferredUsersSuccess,
            getReferralOwnReferredUsersFailed,
          );
        })
        .catch(error => {
          processErrorInResponse(callName, error, action, observer, getReferralOwnReferredUsersFailed);
        });
    },
  )),
);

const getReferralOwnCommissionsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getReferralOwnCommissions.type),
  mergeMap((action: PayloadAction<ReferralPagedRequest | undefined>) => new Observable(
    (observer: Observer<any>) => {
      const callName = 'crm/getReferralOwnCommissions';

      GlobalService
        .crmService
        .getReferralOwnCommissions(action.payload)
        .then(response => {
          processResponse(
            callName,
            action.payload,
            response,
            observer,
            getReferralOwnCommissionsSuccess,
            getReferralOwnCommissionsFailed,
          );
        })
        .catch(error => {
          processErrorInResponse(callName, error, action, observer, getReferralOwnCommissionsFailed);
        });
    },
  )),
);

const createReferralOwnPayoutRequestEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(createReferralOwnPayoutRequest.type),
  mergeMap((action: PayloadAction<CreateReferralPayoutRequest>) => new Observable(
    (observer: Observer<any>) => {
      const callName = 'crm/createReferralOwnPayoutRequest';

      GlobalService
        .crmService
        .createReferralOwnPayoutRequest(action.payload)
        .then(response => {
          processResponse(
            callName,
            action.payload,
            response,
            observer,
            createReferralOwnPayoutRequestSuccess,
            createReferralOwnPayoutRequestFailed,
          );
        })
        .catch(error => {
          processErrorInResponse(callName, error, action, observer, createReferralOwnPayoutRequestFailed);
        });
    },
  )),
);

const refreshReferralProfileAfterPayoutEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(createReferralOwnPayoutRequestSuccess.type, createReferralOwnPayoutRequestFailed.type),
  map(() => getReferralOwnProfile()),
);

/**
 * The referral data is fetched once per session. It does not change while the user is on the
 * site, so a call that already came back is left alone and the data stays in the store until a
 * reload. A failed call is not ready, so it is retried on the next landing.
 */
const REFERRAL_DATA_CALLS = [
  { call: CRMCall.getReferralOwnQr, action: getReferralOwnQr },
  { call: CRMCall.getReferralOwnReferredUsers, action: getReferralOwnReferredUsers },
  { call: CRMCall.getReferralOwnCommissions, action: getReferralOwnCommissions },
];

/**
 * The rest of the referral data only exists once there is a profile, so it is fetched
 * off the back of a successful `own/profile`. A 404 there leaves these calls unmade.
 */
const getReferralDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getReferralOwnProfileSuccess.type),
  mergeMap(() => of(
    ...REFERRAL_DATA_CALLS
      .filter(({ call }) => !isCallStatusReady(call, state$.value.crm.statusByCall))
      .map(({ action }) => action()),
  )),
);

export default combineEpics(
  extendedInfoEpic,
  getReferralOwnProfileEpic,
  getReferralDataEpic,
  getReferralOwnQrEpic,
  getReferralOwnReferredUsersEpic,
  getReferralOwnCommissionsEpic,
  createReferralOwnPayoutRequestEpic,
  refreshReferralProfileAfterPayoutEpic,
  getAccountReferenceIdListEpic,
  getBalancesEpic,
  getCountryListEpic,
  getTradingPlatformsEpic,
  createIndividualEpic,
  activateDemoAccountEpic,
  updateIndividualTransitionFromNewToForVerificationEpic,
  modifyOwnBirthdateEpic,
  searchTradingPlansEpic,
  searchPlanDetailsEpic,
  getAutoPassEpic,
  updateAutoPassEpic,
  createPlanPaymentEpic,
  createPurchasePaymentEpic,
  couponPreviewEpic,
);
